/**
 * ValueProposition Organism Component
 * Content section with graphic (left/right layout)
 */

import React, { useState, useEffect } from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import { Image } from "@/components/atoms/Image";
import { Button } from "@/components/atoms/Button";
import type { ValuePropositionBlok } from "@/types";

export interface ValuePropositionProps {
  blok: ValuePropositionBlok;
}

export const ValueProposition: React.FC<ValuePropositionProps> = ({ blok }) => {
  const {
    heading,
    highlighted_words,
    content,
    graphic,
    graphic_light,
    layout,
    cta_link_text,
    cta_link,
  } = blok;

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const graphicSrc = isDark
    ? graphic?.filename
    : graphic_light?.filename || graphic?.filename;

  // Render heading with highlighted words
  const renderHeading = () => {
    if (!highlighted_words) return heading;

    const wordsToHighlight = highlighted_words.split(",").map((w) => w.trim());
    const pattern = new RegExp(`(${wordsToHighlight.join("|")})`, "gi");
    const parts = heading.split(pattern);

    return parts.map((part, index) => {
      const isHighlighted = wordsToHighlight.some(
        (word) => part.toLowerCase() === word.toLowerCase(),
      );
      return isHighlighted ? (
        <span key={index} className="text-primary dark:text-white">
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      );
    });
  };

  const layoutClasses = {
    "graphic-right": "md:flex-row",
    "graphic-left": "md:flex-row-reverse",
  };

  return (
    <section
      {...storyblokEditable(blok)}
      id={(blok as {anchor?: string}).anchor || undefined}
      className="scroll-mt-24 py-12 md:py-16 lg:py-20 xl:py-24 bg-theme-surface"
    >
      <Container>
        <div
          className={`scroll-mt-24 flex flex-col gap-6 md:gap-12 lg:gap-16 items-center ${layoutClasses[layout]}`}
        >
          {/* Content */}
          <div className="flex-1 flex flex-col gap-4 md:gap-6">
            <Heading level="h4" className="text-black dark:text-primary">
              {renderHeading()}
            </Heading>

            <Text
              size="base"
              color="text-black dark:text-white"
              className="whitespace-pre-line leading-relaxed"
            >
              {content}
            </Text>

            {cta_link_text && (
              <div>
                <Button
                  href={cta_link?.cached_url || cta_link?.url || "#"}
                  variant="black"
                  size="md"
                  className="dark:bg-primary dark:text-black dark:hover:bg-primary/90"
                >
                  {cta_link_text}
                </Button>
              </div>
            )}
          </div>

          {/* Graphic */}
          {graphic && (
            <div className="flex-1 w-full md:max-w-lg lg:max-w-none">
              <Image
                src={graphicSrc}
                alt={graphic.alt || heading}
                className="w-full h-auto mx-auto"
              />
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default ValueProposition;
